import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { API } from "@/config";

/**
 * Lazily-loaded Stripe.js promise. We fetch the publishable key from the
 * BACKEND's /api/checkout/config endpoint instead of shipping it as a
 * frontend build env var — that way the pk always matches the sk used to
 * create the PaymentIntent, and "client_secret does not match any
 * PaymentIntent on this account" errors are architecturally impossible.
 *
 * `resetStripeCache` is exported so the error-boundary refresh button can
 * force a fresh /checkout/config fetch on the next getStripe() call.
 */
let stripePromiseCache = null;

export const getStripe = () => {
  if (stripePromiseCache) return stripePromiseCache;
  stripePromiseCache = axios
    .get(`${API}/checkout/config`)
    .then((r) => {
      if (!r.data?.publishable_key) throw new Error("Missing publishable key");
      // Hard fail if backend + frontend keys are for different Stripe accounts
      // or modes (test vs live). If we proceed, the PaymentElement will silently
      // fail to find any PaymentIntent and render blank on every device —
      // which is the exact class of bug we hit in production.
      if (r.data.keys_match_mode === false) {
        const err = new Error("STRIPE_KEY_MISMATCH");
        err.code = "STRIPE_KEY_MISMATCH";
        err.mode = r.data.mode;
        throw err;
      }
      return loadStripe(r.data.publishable_key);
    })
    .catch((err) => {
      // Reset so a manual refresh can retry.
      stripePromiseCache = null;
      throw err;
    });
  return stripePromiseCache;
};

export const resetStripeCache = () => { stripePromiseCache = null; };
